import { MessageSquare, Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@bigpp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@bigpp/ui/components/dialog";
import { ScrollArea } from "@bigpp/ui/components/scroll-area";
import { Tooltip } from "@bigpp/ui/components/tooltip";
import { cn } from "@bigpp/ui/lib/utils";

import { queryClient, trpc } from "../trpc.ts";

const toDate = (d: Date | string): Date =>
  typeof d === "string" ? new Date(d) : d;

const fmtRelative = (d: Date): string => {
  const diff = Date.now() - d.getTime();
  if (diff < 45_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const fmtAbsolute = (d: Date): string =>
  d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

// Group threads into buckets by last-activity date.
const bucketFor = (d: Date): string => {
  const now = new Date();
  const startOfDay = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const today = startOfDay(now);
  const dayMs = 86_400_000;
  const t = startOfDay(d);
  if (t === today) return "Today";
  if (t === today - dayMs) return "Yesterday";
  if (t > today - 7 * dayMs) return "This week";
  if (t > today - 30 * dayMs) return "Earlier this month";
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
};

export function ThreadsDialog({
  open,
  onOpenChange,
  activeThreadId,
  onPick,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  activeThreadId: string | null;
  onPick: (id: string) => void;
}) {
  const list = trpc.threads.list.useQuery(undefined, { enabled: open });
  const del = trpc.threads.delete.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [["threads", "list"]] });
    },
  });
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const rows = list.data ?? [];
  const groups: { bucket: string; items: typeof rows }[] = [];
  for (const r of rows) {
    const b = bucketFor(toDate(r.updatedAt));
    const last = groups[groups.length - 1];
    if (last && last.bucket === b) last.items.push(r);
    else groups.push({ bucket: b, items: [r] });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Chat history</DialogTitle>
          <DialogDescription>
            Your recent conversations. Pick one to reopen it.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] -mx-1">
          {rows.length === 0 && !list.isLoading && (
            <div className="flex flex-col items-center gap-1 py-10 text-center">
              <MessageSquare className="size-5 text-[var(--color-muted-foreground)]" />
              <p className="text-xs text-[var(--color-muted-foreground)]">
                No conversations yet.
              </p>
            </div>
          )}
          {list.isLoading && (
            <div className="flex flex-col gap-1.5 px-1 py-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-11 animate-pulse rounded-md bg-[var(--color-secondary)]/60"
                />
              ))}
            </div>
          )}
          <div className="flex flex-col gap-3 px-1 pb-1">
            {groups.map((g) => (
              <section key={g.bucket} className="flex flex-col gap-0.5">
                <h3 className="px-1 pt-1 text-[10px] font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
                  {g.bucket}
                </h3>
                <ul className="flex flex-col">
                  {g.items.map((t) => {
                    const isActive = t.id === activeThreadId;
                    const updated = toDate(t.updatedAt);
                    return (
                      <li
                        key={t.id}
                        className={cn(
                          "group flex items-center gap-1 rounded-md px-2 py-1.5 transition-colors",
                          isActive
                            ? "bg-[var(--color-secondary)]"
                            : "hover:bg-[var(--color-secondary)]/60",
                        )}
                      >
                        <button
                          type="button"
                          className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left"
                          onClick={() => {
                            onPick(t.id);
                            onOpenChange(false);
                          }}
                        >
                          <span
                            className={cn(
                              "block max-w-full truncate text-[13px] leading-tight",
                              isActive ? "font-medium" : "",
                            )}
                          >
                            {t.title}
                          </span>
                          <span
                            className="text-[10px] text-[var(--color-muted-foreground)]"
                            title={`Last message ${fmtAbsolute(updated)}`}
                          >
                            {fmtRelative(updated)}
                          </span>
                        </button>
                        <Tooltip label="Delete conversation">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Delete conversation"
                            disabled={pendingDelete === t.id && del.isPending}
                            onClick={() => {
                              setPendingDelete(t.id);
                              del.mutate({ id: t.id });
                            }}
                            className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 data-[active=true]:opacity-100"
                            data-active={isActive ? "true" : undefined}
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </Tooltip>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
